import { useQueryClient } from '@tanstack/react-query'
import { Info } from 'lucide-react'
import React, { useContext, useMemo, useState } from 'react'
import { toast } from 'sonner'

import { getBrokersMembersEnrollmentsQueryKey } from 'components/hooks/needs_attention/useGetBrokersMembersEnrollments'
import { Badge } from 'slab'
import {
  getGetBrokersUnprocessedEnrollmentsIdQueryKey,
  useGetAdminEnrollmentEvent,
  useGetBrokersMembersMemberId
} from 'utils/web_api'
import {
  type BrokersEnrollment,
  type BrokersEnrollmentEnriched,
  type BrokersEnrollmentEnrichedAvailableActionsItem,
  type BrokersEnrollmentState,
} from 'utils/web_api.schemas'

import {
  type EnrollmentSelectOption,
  type EnrollmentSelectOptionGroups,
  UpdateFieldOnChangeFormBoolean,
  UpdateFieldOnChangeFormSelect,
  UpdateFieldOnChangeFormSelectGroups,
  UpdateFieldOnChangeFormText,
} from './EditEnrollmentFormFields'
import { type EnrollmentCoverageType } from './types'
import { type MoneyAmount } from '../../types'
import BrokersMemberContext from '../context/BrokersMemberContext'
import CoverageAuditReasonContext from '../context/CoverageAuditReasonContext'
import {
  type EnrollmentMutateOptions,
  useModifyEnrollment,
} from '../hooks/enrollments/useModifyEnrollment'
import { Banner } from '../reusable/Banner'
import { CollapsibleDataContainer } from '../reusable/CollapsibleDataContainer'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip'

// Type-safe mapping of AASM events to display labels
// TypeScript will enforce that all possible actions have a label
const ENROLLMENT_STATE_ACTION_LABELS: Record<BrokersEnrollmentEnrichedAvailableActionsItem, string> = {
  member_cancel: 'Member Cancel',
  member_confirm: 'Member Confirm',
  member_self_serve_complete: 'Member Self Serve Complete',
  mark_submitted: 'Mark Submitted',
  begin_submission_processing: 'Begin Submission Processing',
  return_to_submission_processing: 'Return To Submission Processing',
  broker_cancel: 'Broker Cancel',
  complete: 'Complete',
  reinstate: 'Reinstate',
  dangerous_move_back_to_member_confirmed: 'Dangerous Move Back To Member Confirmed',
}

// Mapping of enrollment states to badge types and labels
// TypeScript will enforce that all BrokersEnrollmentState keys are present
type BadgeType = React.ComponentProps<typeof Badge>['type']
const ENROLLMENT_STATE_BADGE_CONFIG = {
  in_member_cart: { type: 'slate', label: 'In Member Cart' },
  member_confirmed: { type: 'info', label: 'Member Confirmed' },
  submission_processing: { type: 'warning', label: 'Submission Processing' },
  carrier_processing: { type: 'warning', label: 'Carrier Processing' },
  completed: { type: 'success', label: 'Completed' },
  canceled: { type: 'critical', label: 'Canceled' },
} satisfies Record<BrokersEnrollmentState, { type: BadgeType; label: string }>

type EditableEnrollmentField = {
  field: keyof BrokersEnrollmentEnriched
  label: string
  type: 'string' | 'boolean' | 'money' | 'select' | 'select-groups'
  options?: EnrollmentSelectOptionGroups | EnrollmentSelectOption[]
  tooltip?: React.ReactNode
  requireReason?: boolean
}

const EditQuotedPremiumTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger>
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        Quoted premium is only displayed to users when the enrollment is on
        Reimbursement Only. Otherwise, the latest insurance premium record below
        is displayed.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const EditPlanCodeTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        Plan code can only be updated to text if the enrollment has an
        Off-Thatch category. Otherwise, the plan code MUST be the ID of a
        medical plan within our system. Example: "mdpln_2024-vt-13627vt0380008"
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const StateTransitionTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        Generally brokers should use Workflows to progress the state of an enrollment;
        however, if you need to trigger state changes you may do so using this field.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const AncestorEnrollmentTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        Ancestor Enrollment ID is only necessary when attempt to map a payment
        method from an existing enrollment in the prior year. If no payment
        method needs to be preserved from the prior year, this field can be left
        blank.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const ExchangePolicyIdTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        Exchange Policy ID is only necessary for a select few carriers.
        Currently, this is only BCBS MN and our automated carriers. BCBS MN
        should use the Billing Account ID.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const MaxPremiumReimbursementAmountTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        The Max Premium Reimbursement Amount is applicable to employers with
        Tiered Allowances. This is meant to be the maximum of a specific
        enrollment a member can be reimbursed for, typically set as the cost of
        the member's plan for only the self participant, meaning all dependents
        are not reimbursable.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const ExpectedPaymentDateOffsetTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        The Expected Payment Date Offset field should only be set on enrollments
        with off-cycle payments that cause NAV noise each month. The value can
        be positive or negative, and is the number of days offset from the
        beginning of the month in which coverage starts that we expect a
        payment. Positive values mean payment is expected after the start of the
        month, negative values mean payment is expected before the start of the
        month.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const CarrierMemberIdTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        The Carrier Member ID is the ID of the member in the carrier's system.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const PaymentMethodIdTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        The Stripe Issuing Card ID associated with this enrollment's recurring
        obligation. This identifies the payment method used for this enrollment.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

const AutopayStateTooltip = () => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger
        tabIndex={-1}
        onFocus={(e) => {
          e.preventDefault()
        }}
      >
        <Info size={16} />
      </TooltipTrigger>
      <TooltipContent className='w-96'>
        The autopay state indicates whether automatic payments are set up for
        this enrollment. Unknown means we're not sure about the autopay status,
        Setup Required means autopay needs to be configured, On means autopay is
        active, and Off means autopay is disabled.
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
)

export const manualSubmissionMethodOptions: EnrollmentSelectOption[] = [
  { value: 'manual', label: 'Manual' },
  { value: 'off_thatch', label: 'Off-Thatch' },
  // NOTE: auto_renewal and member_plan_change_with_carrier are deprecated and no longer assigned by system
]

export const automatedSubmissionMethodOptions: EnrollmentSelectOption[] = [
  { value: 'anthem', label: 'Anthem' },
  { value: 'kaiser', label: 'Kaiser' },
  { value: 'oscar', label: 'Oscar' },
  { value: 'vsp_browser_automation', label: 'VSP' },
  { value: 'renaissance_browser_automation', label: 'Renaissance' },
  { value: 'guardian_browser_automation', label: 'Guardian' },
  { value: 'uhc_browser_automation', label: 'UHC' },
  { value: 'ambetter_embark', label: 'Ambetter Embark' },
  { value: 'ambetter_equity', label: 'Ambetter Equity' },
  { value: 'bsca', label: 'Blue Shield of California' },
  { value: 'health_sherpa_automation', label: 'Health Sherpa' },
]

const expectedPaymentDateOffsetOptions: EnrollmentSelectOption[] = Array.from(
  { length: 61 },
  (_, i) => i - 30
).map((value) => ({
  value: value.toString(),
  label: value.toString(),
}))

// Generate reimbursement date options: 12 months past and 12 months future (first of each month)
const generateReimbursementDateOptions = (): EnrollmentSelectOptionGroups => {
  const today = new Date()
  const pastOptions: EnrollmentSelectOption[] = []
  const futureOptions: EnrollmentSelectOption[] = []

  // Generate 12 months in the past
  for (let i = 12; i >= 1; i--) {
    const date = new Date(today.getFullYear(), today.getMonth() - i, 1)
    const isoString = date.toISOString().split('T')[0] // YYYY-MM-DD format
    const displayString = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
    pastOptions.push({
      value: isoString,
      label: displayString,
    })
  }

  // Generate 12 months in the future
  for (let i = 0; i < 12; i++) {
    const date = new Date(today.getFullYear(), today.getMonth() + i, 1)
    const isoString = date.toISOString().split('T')[0] // YYYY-MM-DD format
    const displayString = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
    })
    futureOptions.push({
      value: isoString,
      label: displayString,
    })
  }

  return {
    past: {
      label: 'Past Months',
      options: pastOptions,
    },
    future: {
      label: 'Future Months',
      options: futureOptions,
    },
  }
}

const reimbursementOnlyAtOptions: EnrollmentSelectOptionGroups = {
  clear: {
    label: 'Clear Value',
    options: [{ value: 'nil', label: 'Clear (set to nil)' }],
  },
  ...generateReimbursementDateOptions(),
}

const baseEditableEnrollmentFields: EditableEnrollmentField[] = [
  {
    field: 'plan_code',
    label: 'Plan code',
    type: 'string',
    tooltip: <EditPlanCodeTooltip />,
  },
  {
    field: 'off_exchange',
    label: 'Off Exchange',
    type: 'boolean',
    requireReason: true,
  },
  {
    field: 'quoted_premium',
    label: 'Quoted Premium ($)',
    type: 'money',
    tooltip: <EditQuotedPremiumTooltip />,
  },
  {
    field: 'reimbursement_only_at',
    label: 'Reimbursement Only At',
    type: 'select-groups',
    options: reimbursementOnlyAtOptions,
    requireReason: true,
  },
  {
    field: 'submission_method',
    label: 'Submission Method',
    type: 'select-groups',
    options: {
      automated: {
        label: 'Manual',
        options: manualSubmissionMethodOptions,
      },
      manual: {
        label: 'Automated',
        options: automatedSubmissionMethodOptions,
      },
    },
  },
  {
    field: 'autopay_state',
    label: 'Autopay State',
    type: 'select',
    options: [
      { value: 'unknown', label: 'Unknown' },
      { value: 'setup_required', label: 'Setup Required' },
      { value: 'on', label: 'On' },
      { value: 'off', label: 'Off' },
    ],
    tooltip: <AutopayStateTooltip />,
  },
  {
    field: 'ancestor_enrollment_id',
    label: 'Ancestor Enrollment ID',
    type: 'string',
    tooltip: <AncestorEnrollmentTooltip />,
  },
  {
    field: 'exchange_policy_id',
    label: 'Exchange Policy ID',
    type: 'string',
    tooltip: <ExchangePolicyIdTooltip />,
  },
  {
    field: 'expected_payment_date_offset',
    label: 'Expected Payment Date Offset',
    type: 'select',
    options: expectedPaymentDateOffsetOptions,
    tooltip: <ExpectedPaymentDateOffsetTooltip />,
  },
  {
    field: 'payment_method_id',
    label: 'Stripe Issuing Card ID',
    type: 'string',
    tooltip: <PaymentMethodIdTooltip />,
  },
  {
    field: 'carrier_member_id',
    label: 'Carrier Member ID',
    type: 'string',
    tooltip: <CarrierMemberIdTooltip />,
  },
]

const getAdminOnlyEditableEnrollmentFields = (
  enrollmentEventOptions: EnrollmentSelectOption[]
): EditableEnrollmentField[] => [
  {
    field: 'enrollment_event_id',
    label: 'Enrollment Event ID',
    type: 'select',
    options: enrollmentEventOptions,
  },
]

const tieredAllowancesEditableEnrollmentFields: EditableEnrollmentField[] = [
  {
    field: 'max_premium_reimbursement_amount',
    label: 'Max Premium Reimbursement Amount ($)',
    type: 'money',
    tooltip: <MaxPremiumReimbursementAmountTooltip />,
  },
]

const medicalCategoryOptions: EditableEnrollmentField[] = [
  {
    field: 'category',
    label: 'Category',
    type: 'select',
    requireReason: true,
    options: [
      { value: 'general_medical', label: 'General Medical' },
      { value: 'off_thatch_medical', label: 'Off-Thatch Medical' },
      { value: 'medicare', label: 'Medicare' },
      { value: 'medicare_part_a', label: 'Medicare (Part A)' },
      { value: 'medicare_part_b', label: 'Medicare (Part B)' },
      { value: 'medicare_part_c', label: 'Medicare (Part C)' },
      { value: 'medicare_part_d', label: 'Medicare (Part D)' },
      { value: 'medicare_supplement', label: 'Medicare Supplement' },
      { value: 'subsidy', label: 'Subsidy' },
      { value: 'cobra', label: 'COBRA' },
    ],
  },
]

const dentalCategoryOptions: EditableEnrollmentField[] = [
  {
    field: 'category',
    label: 'Category',
    type: 'select',
    requireReason: true,
    options: [
      { value: 'guardian_dental', label: 'Guardian' },
      { value: 'renaissance_dental', label: 'Renaissance' },
      { value: 'bsca_dental', label: 'BlueShield of California' },
      { value: 'off_thatch_dental', label: 'Off-Thatch Dental' },
    ],
  },
]

const visionCategoryOptions: EditableEnrollmentField[] = [
  {
    field: 'category',
    label: 'Category',
    type: 'select',
    requireReason: true,
    options: [
      { value: 'vsp', label: 'VSP' },
      { value: 'bsca_vision', label: 'BlueShield of California' },
      { value: 'off_thatch_vision', label: 'Off-Thatch Vision' },
    ],
  },
]

function getCategoryOptions (coverageType: EnrollmentCoverageType) {
  if (coverageType === 'medical') {
    return medicalCategoryOptions
  }

  if (coverageType === 'dental') {
    return dentalCategoryOptions
  }

  if (coverageType === 'vision') {
    return visionCategoryOptions
  }

  return []
}

function getEditableEnrollmentFields (
  isTieredAllowancesEmployer: boolean | undefined,
  coverageType: EnrollmentCoverageType,
  isUserThatchAdmin: boolean | undefined = false,
  enrollmentEventOptions: EnrollmentSelectOption[] = []
) {
  const categoryOptions = getCategoryOptions(coverageType)
  const adminFields = isUserThatchAdmin
    ? getAdminOnlyEditableEnrollmentFields(enrollmentEventOptions)
    : []

  // State transition field (placed before category)
  const stateTransitionField: EditableEnrollmentField = {
    field: 'state', // Note: This maps to state_action API parameter, not the state field
    label: 'State Transition',
    type: 'select-groups',
    options: {}, // Will be populated dynamically from available_actions
    tooltip: <StateTransitionTooltip />,
  }

  if (isTieredAllowancesEmployer && coverageType === 'medical') {
    return [
      stateTransitionField,
      ...categoryOptions,
      ...baseEditableEnrollmentFields,
      ...tieredAllowancesEditableEnrollmentFields,
      ...adminFields,
    ]
  }

  return [stateTransitionField, ...categoryOptions, ...baseEditableEnrollmentFields, ...adminFields]
}

type EditEnrollmentDataProps = {
  enrollment: BrokersEnrollmentEnriched
}

// EditEnrollmentData is a component that allows the user to edit the data for an enrollment.
function EditEnrollmentData ({ enrollment }: EditEnrollmentDataProps) {
  const queryClient = useQueryClient()
  const { memberId, isTieredAllowancesEmployer, isUserThatchAdmin } =
    useContext(BrokersMemberContext)
  const { reason } = useContext(CoverageAuditReasonContext)

  // Fetch member data to get employee_id
  const { data: memberData } = useGetBrokersMembersMemberId(memberId, {
    query: { enabled: !!memberId },
  })

  // Fetch enrollment events for admin users
  const { data: enrollmentEventsData } = useGetAdminEnrollmentEvent(
    { employee_id: memberData?.employee_id || '' },
    {
      query: {
        enabled: isUserThatchAdmin && !!memberData?.employee_id,
      },
    }
  )

  // Create enrollment event options for the select component
  const enrollmentEventOptions = useMemo(() => {
    if (!enrollmentEventsData?.data) return []

    return enrollmentEventsData.data.map((event) => {
      const startDate = new Date(event.enrollment_window_start_date)
      const dateLabel = startDate.toLocaleDateString('en-US', {
        month: '2-digit',
        year: '2-digit',
      })

      const eventType = event.enrollment_type
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (l) => l.toUpperCase())

      return {
        value: event.id,
        label: `${eventType} - ${dateLabel}`,
      }
    })
  }, [enrollmentEventsData])

  const modifyEnrollmentMutation = useModifyEnrollment({
    enrollmentId: enrollment.id,
    memberId: enrollment.member_id,
  })
  const [enrollmentError, setEnrollmentError] = useState<string | null>(null)

  const getModifyEnrollmentMutationOptions = (
    field: keyof BrokersEnrollmentEnriched,
    value: string
  ): EnrollmentMutateOptions => {
    return {
      onSuccess: (data) => {
        // NOTE: The modifyEnrollment mutation returns the updated enrollment data that's associated with
        // the broker portal, not the transactions view. That's why we can update one query's cache but need
        // to invalidate the other.
        // TODO(@tutt-thatch): Use the same Grape Entity for both views.

        // Update the query data for the enrollment in the broker portal.
        queryClient.setQueryData(
          getGetBrokersUnprocessedEnrollmentsIdQueryKey(enrollment.id),
          data
        )
        // Invalidate the query for enrollments info in the transactions view for a member.
        queryClient.invalidateQueries({
          queryKey: getBrokersMembersEnrollmentsQueryKey({ memberId }),
        })
        toast.success(`Successfully updated ${field} to ${value}`, {
          position: 'top-center',
        })
        setEnrollmentError(null)
      },
      onError: (error) => {
        setEnrollmentError(
          `Error updating ${field} to ${value}: ${
            error.errors.join(', ') ?? error.message
          }`
        )
      },
    }
  }

  const onUpdateBoolean = (
    field: keyof BrokersEnrollmentEnriched,
    value: boolean
  ) => {
    const payload: any = { [field]: value }
    // Add reason if available (form components ensure reason exists when required)
    if (reason) {
      payload.coverage_audit_reason = reason
    }

    modifyEnrollmentMutation.mutate(
      payload,
      getModifyEnrollmentMutationOptions(field, value.toString())
    )
  }

  const onUpdateString = (
    field: keyof BrokersEnrollmentEnriched,
    value: string
  ) => {
    // Special case: 'state' field triggers a state_action (not updating the state field directly)
    // The enrollment.state field is read-only; state changes happen via AASM events
    if (field === 'state') {
      const payload: any = { state_action: value }
      if (reason) {
        payload.coverage_audit_reason = reason
      }

      modifyEnrollmentMutation.mutate(
        payload,
        getModifyEnrollmentMutationOptions(field, value)
      )
      return
    }

    // Handle special case for reimbursement_only_at: nil should be null
    const processedValue =
      field === 'reimbursement_only_at' && value === 'nil' ? null : value

    const payload: any = { [field]: processedValue }
    // Add reason if available (form components ensure reason exists when required)
    if (reason) {
      payload.coverage_audit_reason = reason
    }

    modifyEnrollmentMutation.mutate(
      payload,
      getModifyEnrollmentMutationOptions(field, value)
    )
  }

  const successContent = (
    <div className='p-4'>
      {enrollmentError && (
        <Banner
          type='critical'
          titleText='Error updating enrollment'
          className='my-2'
        >
          {enrollmentError}
        </Banner>
      )}
      <div>
        {/* Current State Display (read-only badge) */}
        <div className='flex justify-between items-center mb-2'>
          <label className='w-1/3 whitespace-nowrap text-ellipsis overflow-hidden'>
            State
          </label>
          <div className='w-2/3 flex justify-end items-center space-x-1'>
            {(() => {
              const config = ENROLLMENT_STATE_BADGE_CONFIG[enrollment.state] || {
                type: 'slate',
                label: enrollment.state,
              }
              return <Badge type={config.type}>{config.label}</Badge>
            })()}
          </div>
        </div>

        {getEditableEnrollmentFields(
          isTieredAllowancesEmployer,
          enrollment.coverage as EnrollmentCoverageType,
          isUserThatchAdmin,
          enrollmentEventOptions
        ).map(({ field, label, type, options, tooltip, requireReason }) => {
          // Handle state transition field dynamically
          if (field === 'state') {
            // Only show if there are available actions
            if (!enrollment.available_actions || enrollment.available_actions.length === 0) {
              return null
            }

            const stateOptions: EnrollmentSelectOption[] = enrollment.available_actions.map((action) => ({
              value: action,
              label: ENROLLMENT_STATE_ACTION_LABELS[action],
            }))

            // Use select-groups format for wider display (w-2/3 instead of w-1/2)
            const stateGroups: EnrollmentSelectOptionGroups = {
              actions: {
                label: 'Available Actions',
                options: stateOptions,
              },
            }

            return (
              <UpdateFieldOnChangeFormSelectGroups
                key={field}
                field={field}
                value='' // No current value since this triggers an action
                label={label}
                groups={stateGroups}
                onChange={onUpdateString}
                tooltip={tooltip}
                requireReason={requireReason}
              />
            )
          }

          if (type === 'boolean') {
            return (
              <UpdateFieldOnChangeFormBoolean
                key={field}
                field={field}
                value={enrollment[field as keyof BrokersEnrollment] as boolean}
                label={label}
                onChange={onUpdateBoolean}
                requireReason={requireReason}
              />
            )
          }

          if (type === 'select') {
            return (
              <UpdateFieldOnChangeFormSelect
                key={field}
                field={field}
                value={
                  enrollment[
                    field as keyof BrokersEnrollment
                  ]?.toString() as string
                }
                label={label}
                options={options as EnrollmentSelectOption[]}
                onChange={onUpdateString}
                tooltip={tooltip}
                requireReason={requireReason}
              />
            )
          }

          if (type === 'select-groups') {
            // Handle special case for reimbursement_only_at.
            const fieldValue = enrollment[field as keyof BrokersEnrollment]
            let displayValue: string

            if (field === 'reimbursement_only_at') {
              // Format the date to YYYY-MM-DD.
              displayValue = fieldValue?.toString().split('T')[0] || ''
            } else {
              displayValue = fieldValue?.toString() || ''
            }

            return (
              <UpdateFieldOnChangeFormSelectGroups
                key={field}
                field={field}
                value={displayValue}
                label={label}
                groups={options as EnrollmentSelectOptionGroups}
                onChange={onUpdateString}
                requireReason={requireReason}
              />
            )
          }

          if (type === 'money') {
            const moneyAmount = enrollment[
              field as keyof BrokersEnrollment
            ] as MoneyAmount

            return (
              <UpdateFieldOnChangeFormText
                key={field}
                field={field}
                value={moneyAmount?.value as string}
                label={label}
                isMoneyAmount
                onChange={onUpdateString}
                tooltip={tooltip}
              />
            )
          }

          return (
            <UpdateFieldOnChangeFormText
              key={field}
              field={field}
              value={
                enrollment[field as keyof BrokersEnrollment]?.toString() || ''
              }
              label={label}
              onChange={onUpdateString}
              tooltip={tooltip}
            />
          )
        })}
      </div>
    </div>
  )

  return (
    <CollapsibleDataContainer
      heading='Enrollment'
      isLoading={modifyEnrollmentMutation.isPending}
      loadingContent={successContent}
      error={enrollmentError}
      localStorageKey='edit-enrollment-data'
      successContent={successContent}
    />
  )
}

export default EditEnrollmentData
